import { useState } from 'react'
import { Button } from '../../ui/button'
import { Icon } from '../../ui/icon'
import { InputText } from '../../ui/input-text'

interface PersonalInfoScreenProps {
	onNext: (data: Record<string, any>) => void
	onBack: () => void
	data: Record<string, any>
}

export function PersonalInfoScreen({
	onNext,
	onBack,
	data,
}: PersonalInfoScreenProps) {
	const [formData, setFormData] = useState({
		fullName: data.fullName || '',
		birthDate: data.birthDate || '',
		postalCode: data.postalCode || '',
		phone: data.phone || '',
		email: data.email || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const validateEmail = (email: string) => {
		const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return regex.test(email)
	}

	const validateDate = (dateStr: string) => {
		const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/
		const match = dateStr.match(regex)
		if (!match) return false

		const day = parseInt(match[1], 10)
		const month = parseInt(match[2], 10)
		const year = parseInt(match[3], 10)

		if (month < 1 || month > 12) return false
		if (day < 1 || day > 31) return false

		const date = new Date(year, month - 1, day)
		if (
			date.getDate() !== day ||
			date.getMonth() !== month - 1 ||
			date.getFullYear() !== year
		) {
			return false
		}

		// Validate minimum age (18 years)
		const today = new Date()
		const age = today.getFullYear() - year
		if (age < 18) return false
		if (age === 18) {
			if (today.getMonth() < month - 1) return false
			if (today.getMonth() === month - 1 && today.getDate() < day) return false
		}

		return true
	}

	const formatBirthDate = (value: string) => {
		// Remove non-digits
		const cleaned = value.replace(/\D/g, '')

		// Format as DD/MM/YYYY
		if (cleaned.length <= 2) {
			return cleaned
		} else if (cleaned.length <= 4) {
			return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`
		} else {
			return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`
		}
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.fullName) newErrors.fullName = 'Ingresa tu nombre'
		if (!formData.birthDate) {
			newErrors.birthDate = 'Ingresa tu fecha de nacimiento'
		} else if (!validateDate(formData.birthDate)) {
			newErrors.birthDate = 'Fecha inválida o menor de 18 años'
		}
		if (!formData.postalCode) newErrors.postalCode = 'Ingresa tu código postal'
		if (!formData.phone) newErrors.phone = 'Ingresa tu teléfono'
		if (!formData.email) {
			newErrors.email = 'Ingresa tu email'
		} else if (!validateEmail(formData.email)) {
			newErrors.email = 'Ingresa un email válido'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.fullName &&
		formData.birthDate &&
		formData.postalCode &&
		formData.phone &&
		formData.email

	return (
		<div className="flex w-full flex-col gap-6 pb-20 md:gap-8 md:pb-0">
			{/* Form header */}
			<div className="flex w-full flex-col gap-2 border-b border-neutral-400 pb-4">
				<h2 className="font-parkinsans text-[20px] leading-7 font-medium text-blue-800 md:text-[24px] md:leading-8">
					Sobre vos
				</h2>
				<p className="font-parkinsans text-sm leading-6 font-normal text-neutral-900 md:text-base md:leading-[30px]">
					Verifica si tus datos personales son correctos
				</p>
			</div>

			{/* Form fields */}
			<div className="flex w-full flex-col gap-4 md:gap-3">
				{/* Row 1: Nombre + Fecha de nacimiento */}
				<div className="flex w-full flex-col gap-4 md:flex-row md:gap-8">
					<div className="w-full md:flex-1">
						<InputText
							label="Nombre"
							labelPosition="up"
							size="m"
							placeholder="Franco Medina"
							value={formData.fullName}
							onChange={(e) => {
								setFormData({ ...formData, fullName: e.target.value })
								setErrors({ ...errors, fullName: '' })
							}}
							errorText={errors.fullName}
							required
						/>
					</div>
					<div className="w-full md:flex-1">
						<InputText
							label="Fecha de nacimiento"
							labelPosition="up"
							size="m"
							placeholder="DD/MM/AAAA"
							value={formData.birthDate}
							onChange={(e) => {
								const formatted = formatBirthDate(e.target.value)
								setFormData({ ...formData, birthDate: formatted })
								setErrors({ ...errors, birthDate: '' })
							}}
							errorText={errors.birthDate}
							maxLength={10}
						/>
					</div>
				</div>

				{/* Row 2: Código postal + Teléfono */}
				<div className="flex w-full flex-col gap-4 md:flex-row md:gap-8">
					<div className="w-full md:flex-1">
						<InputText
							label="Código postal"
							labelPosition="up"
							size="m"
							placeholder="5000"
							value={formData.postalCode}
							onChange={(e) => {
								setFormData({
									...formData,
									postalCode: e.target.value.replace(/\D/g, ''),
								})
								setErrors({ ...errors, postalCode: '' })
							}}
							errorText={errors.postalCode}
							required
						/>
					</div>
					<div className="w-full md:flex-1">
						<InputText
							label="Teléfono"
							labelPosition="up"
							size="m"
							type="tel"
							placeholder="3515920792"
							value={formData.phone}
							onChange={(e) => {
								setFormData({ ...formData, phone: e.target.value })
								setErrors({ ...errors, phone: '' })
							}}
							errorText={errors.phone}
							required
						/>
					</div>
				</div>

				{/* Row 3: Email */}
				<div className="flex w-full">
					<div className="w-full">
						<InputText
							label="Email"
							labelPosition="up"
							size="m"
							type="email"
							placeholder="franco.medina@gmail.com"
							value={formData.email}
							onChange={(e) => {
								setFormData({ ...formData, email: e.target.value })
								setErrors({ ...errors, email: '' })
							}}
							errorText={errors.email}
							required
						/>
					</div>
				</div>
			</div>

			{/* Footer buttons - Desktop only */}
			<div className="hidden w-full flex-col-reverse items-center justify-between gap-3 md:flex md:flex-row md:gap-0">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="primary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>

			{/* Mobile footer - Fixed at bottom */}
			<div className="fixed right-0 bottom-0 left-0 z-10 border-t border-neutral-400 bg-white px-6 py-3 md:hidden">
				<div className="flex items-center justify-between">
					<Button
						variant="primary"
						appearance="ghost"
						size="m"
						onClick={onBack}
						className="gap-2 rounded-xl px-4 py-3"
					>
						<Icon name="arrow-left" className="size-4" />
						Atrás
					</Button>
					<Button
						variant="primary"
						appearance="solid"
						size="m"
						onClick={handleSubmit}
						disabled={!isFormValid}
						className="gap-2 rounded-xl px-4 py-3"
					>
						Siguiente
						<Icon name="arrow-right" className="size-3.5" />
					</Button>
				</div>
			</div>
		</div>
	)
}
